import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import {
  addPartner,
  deleteCourseModuleVideo,
  getBatches,
  getManagedPartner,
  getPartner,
  getPartners,
  getSelfPacedCourse,
  getSelfPacedCourseModules,
  updateCourseModule,
  updatePartner,
} from "@/api";
import { getPartnerCountries } from "@/api/partners";
import type { PartnerListFilters } from "@/api/partners";
import type { Partner, User } from "@/types";

export const partnersQueryKeys = {
  usePartner: (partnerId?: Partner["id"]) => [
    "partners",
    "getPartner",
    partnerId,
  ],
  useManagedPartner: () => ["partners", "getPartners"],
  usePartners: () => ["partners", "getPartners"],
  usePartnerCountries: () => ["partners", "countries"],
  usePartnersPage: (page: number, filters: string, sort?: string) => [
    "partners",
    "getPartners",
    page,
    filters,
    sort,
  ],
  useBatchesPage: (page: number) => ["batches", "getBatch", page],
  useSelfPacedCourse: (partnerId?: Partner["id"]) => [
    "courses",
    "getSelfPacedCourse",
    partnerId,
  ],
  useSelfPacedCourseModules: (partnerId?: Partner["id"]) => [
    "courses",
    "getSelfPacedCourseModules",
    partnerId,
  ],
};

export const usePartners = () => {
  const useAllPartners = (
    page: number,
    filters: PartnerListFilters,
    sort?: string,
  ) =>
    useQuery({
      queryFn: () => getPartners(page, filters, sort),
      queryKey: partnersQueryKeys.usePartnersPage(
        page,
        JSON.stringify(filters),
        sort,
      ),
      placeholderData: keepPreviousData,
    });

  const usePartnerCountries = () =>
    useQuery({
      queryFn: getPartnerCountries,
      queryKey: partnersQueryKeys.usePartnerCountries(),
      staleTime: 1000 * 60 * 60,
    });

  const usePartner = (partnerId: Partner["id"] | undefined) =>
    useQuery({
      queryFn: () => getPartner(partnerId),
      queryKey: partnersQueryKeys.usePartner(partnerId),
      enabled: !!partnerId,
    });

  const useManagedPartner = (userId: User["id"] | undefined) =>
    useQuery({
      queryFn: () => getManagedPartner(userId),
      queryKey: partnersQueryKeys.useManagedPartner(),
      enabled: !!userId,
    });

  const useAddPartner = () =>
    useMutation({
      mutationFn: addPartner,
    });

  const useUpdatePartner = () => useMutation({ mutationFn: updatePartner });

  const useAllBatches = (partnerId: Partner["id"], page: number) =>
    useQuery({
      queryFn: () => getBatches(partnerId, page),
      queryKey: partnersQueryKeys.useBatchesPage(page),
    });

  const useSelfPacedCourse = (partnerId: Partner["id"], options = {}) =>
    useQuery({
      queryFn: () => getSelfPacedCourse(partnerId),
      queryKey: partnersQueryKeys.useSelfPacedCourse(partnerId),
      enabled: !!partnerId,
      ...options,
    });

  const useSelfPacedCourseModules = (partnerId: Partner["id"], options = {}) =>
    useQuery({
      queryFn: () => getSelfPacedCourseModules(partnerId),
      queryKey: partnersQueryKeys.useSelfPacedCourseModules(partnerId),
      enabled: !!partnerId,
      ...options,
    });

  const useUpdateCourseModule = () =>
    useMutation({ mutationFn: updateCourseModule });

  const useDeleteCourseModuleVideo = () =>
    useMutation({ mutationFn: deleteCourseModuleVideo });

  return {
    useAllPartners,
    usePartnerCountries,
    useAddPartner,
    usePartner,
    useManagedPartner,
    useUpdatePartner,
    useAllBatches,
    useSelfPacedCourse,
    useSelfPacedCourseModules,
    useUpdateCourseModule,
    useDeleteCourseModuleVideo,
  };
};
